/**
 * AI Foundry Service — System Web
 *
 * Provides a SquirrelMessageSender adapter for the shared SquirrelInterviewModal.
 * Routes all AI calls through the backend proxy at /api/ai/agent.
 *
 * Context: purpose='library' — triggers extended system prompt that collects
 * polymorphic structure, jurisdiction scope, and visibility requirements in
 * addition to the standard department template fields.
 */

import { apiFetch, apiPostFormData } from '../apiClient';
import type {
  SquirrelChatMessage,
  SquirrelSendResult,
  SquirrelInterviewContext,
  ExtractedMetadata,
} from '@firesquirrel/ui-admin';

// ---------------------------------------------------------------------------
// Chat surface routing
// The frontend sends the UI surface as app context; the backend derives the
// route (Unified AI Architecture §5.1 — no frontend agentType mapping).
// ---------------------------------------------------------------------------

const SURFACE_CONVERSATION = 'template-interview';
const SURFACE_EXTRACTION   = 'template-metadata';
const SURFACE_GENERATION   = 'template-generate';

// ---------------------------------------------------------------------------
// Outcome-driven system prompt — library edition
// Identical logic to tenant-web but always uses library mode.
// ---------------------------------------------------------------------------

function buildSystemPrompt(_context: SquirrelInterviewContext): string {
  const basePrompt = `You are Squirrel, an expert fire inspection consultant designing inspection templates for a shared library.

## Context — This Is a Library Template
You are NOT building a template for a specific fire department.
You ARE designing a reusable, published inspection template that any department in the country can adopt from the library.
Think of this like designing a product — not customizing something for a client.
Frame all questions around: "What should this template define as the standard?"

## Your Personality
- Knowledgeable, practical, conversational — think 30-year firefighter turned software consultant
- Ask 1–2 focused questions per turn, never dump a list of questions at once
- Reflect back what you hear ("So this is a standard commercial kitchen suppression inspection — covers hood systems, Ansul, monthly tags. Correct?")
- Make informed assumptions based on common fire inspection practice and ask for confirmation
- Adjust depth based on complexity: a hydrant flow test needs fewer fields than a multi-story mixed-use

## Required Outcomes
You MUST collect all of the following before signalling completion.
Do NOT signal complete until every item is confidently known:

INSPECTION SCOPE
- What type of property or hazard is this template for? (occupancy type, property category, hazard scenario)
- Does this template cover a single inspection type, or does it adapt based on a selected sub-type?
  e.g., "AB 38 — Parcel only" vs "AB 38 — Parcel + Structure"

FIELD DESIGN
- What are the key inspection items? What does a pass vs. fail look like for each?
- What does the inspector observe (toggle pass/fail) vs. measure (number/distance) vs. note (free text)?
- What property data is system-provided (address, APN, owner, building type) vs. must be collected in the field?

WORKFLOW DEFAULTS
- What should trigger a reinspection by default? (violation found, severity threshold, time limit)
- Should the template allow owner self-certification of corrections, or require a physical reinspection?
- Should access denial be a distinct recorded outcome?

COMPLIANCE
- Photo standard: always required, required only on violations, optional, or none?
- Signature standard: inspector only, or also property owner / responsible party?
- Applicable code references? (IFC section, NFPA chapter, PRC 4291, local ordinance, or jurisdiction-neutral)

LIBRARY SCOPE
- Geographic scope: nationwide, specific states, or specific agency types (municipal, county, fire district)?
- Is this template designed to a specific code edition (e.g. IFC 2021) or kept edition-neutral?
- Severity tracking: none, pass/fail only, or minor/major/critical?
- Should offline field use be the default for this template type?`;

  const dataBindingRule = `

## Critical: Data-Bound Widgets
NEVER propose a plain text field when a bound widget exists:
- APN → entity.apn
- Property address → entity.address
- Owner name → entity.ownerName
- Requesting party → contact widget (create-on-the-fly capable)

Plain text is ONLY for: free-form observations, corrective action notes, fields with no system equivalent.`;

  const completionSignal = `

## Signalling Completion
When you have collected ALL required outcomes above, end your message with exactly:
[SQUIRREL_COMPLETE]

This token tells the system to extract structured metadata from our conversation.
Do NOT include it until you are genuinely ready to build the template.`;

  return basePrompt + dataBindingRule + completionSignal;
}

// ---------------------------------------------------------------------------
// Extraction prompt
// ---------------------------------------------------------------------------

const EXTRACTION_PROMPT = `You extract structured inspection template metadata from a completed conversation.

Return ONLY a single valid JSON object — no markdown, no explanation, no wrapper.

Required shape:
{
  "templateName": "string — clear, descriptive name",
  "description": "string — 1-2 sentences",
  "templateType": "wildland | fireCode | hydrant | complex | communityRisk | custom",
  "polymorphicType": "string | null — e.g. 'AB38 Parcel' or null if not polymorphic",
  "suggestedFields": [
    { "label": "string", "type": "string", "required": true, "dataBinding": "entity.address | null" }
  ],
  "regulatoryReferences": ["string"],
  "photoRequirements": "required | optional | none",
  "signatureRequirements": ["inspector", "propertyOwner"],
  "offlineCapable": true,
  "severityTracking": true,
  "reinspectionTriggers": ["string"],
  "visibility": "tenant | organization | system"
}

Rules:
- Prefer data-bound widgets over plain text fields
- regulatoryReferences: include section numbers when known (e.g. "IFC 2021 §315")
- visibility: use "system" for library templates, "tenant" for department-only
- polymorphicType: describe the primary dimension of variation if the template adapts by type`;

// ---------------------------------------------------------------------------
// Generation prompt — one-shot JSON form structure from completed conversation
// ---------------------------------------------------------------------------

const WIDGET_CATALOGUE = [
  { widgetId: 'entity.name', label: 'Entity Name', dataSource: 'inspectableEntity' },
  { widgetId: 'entity.address.full', label: 'Full Address', dataSource: 'inspectableEntity' },
  { widgetId: 'entity.address.street', label: 'Street Address', dataSource: 'inspectableEntity' },
  { widgetId: 'entity.address.city', label: 'City', dataSource: 'inspectableEntity' },
  { widgetId: 'entity.address.state', label: 'State', dataSource: 'inspectableEntity' },
  { widgetId: 'entity.address.zip', label: 'ZIP Code', dataSource: 'inspectableEntity' },
  { widgetId: 'entity.parcelNumber', label: 'Parcel Number', dataSource: 'inspectableEntity' },
  { widgetId: 'entity.propertyType', label: 'Property Type', dataSource: 'inspectableEntity' },
  { widgetId: 'entity.zoning', label: 'Zoning', dataSource: 'inspectableEntity' },
  { widgetId: 'entity.inspectionFrequency', label: 'Inspection Frequency', dataSource: 'inspectableEntity' },
  { widgetId: 'entity.lastInspectionDate', label: 'Last Inspection Date', dataSource: 'inspectableEntity' },
  { widgetId: 'entity.nextScheduledInspection', label: 'Next Scheduled Inspection', dataSource: 'inspectableEntity' },
  { widgetId: 'entity.primaryContact.name', label: 'Primary Contact Name', dataSource: 'inspectableEntity' },
  { widgetId: 'entity.primaryContact.phone', label: 'Primary Contact Phone', dataSource: 'inspectableEntity' },
  { widgetId: 'entity.primaryContact.email', label: 'Primary Contact Email', dataSource: 'inspectableEntity' },
  { widgetId: 'entity.building.buildingNumber', label: 'Building Number', dataSource: 'inspectableEntity' },
  { widgetId: 'entity.building.yearBuilt', label: 'Year Built', dataSource: 'inspectableEntity' },
  { widgetId: 'entity.building.squareFeet', label: 'Square Footage', dataSource: 'inspectableEntity' },
  { widgetId: 'entity.building.floors', label: 'Number of Floors', dataSource: 'inspectableEntity' },
  { widgetId: 'entity.building.constructionType', label: 'Construction Type', dataSource: 'inspectableEntity' },
  { widgetId: 'entity.building.sprinklered', label: 'Sprinklered', dataSource: 'inspectableEntity' },
  { widgetId: 'entity.building.buildingType', label: 'Building Type', dataSource: 'inspectableEntity' },
  { widgetId: 'entity.hydrant.hydrantNumber', label: 'Hydrant Number', dataSource: 'inspectableEntity' },
  { widgetId: 'entity.hydrant.hydrantType', label: 'Hydrant Type', dataSource: 'inspectableEntity' },
  { widgetId: 'entity.hydrant.flowRate', label: 'Flow Rate (GPM)', dataSource: 'inspectableEntity' },
  { widgetId: 'entity.hydrant.outlets', label: 'Number of Outlets', dataSource: 'inspectableEntity' },
  { widgetId: 'entity.parcel.acres', label: 'Acres', dataSource: 'inspectableEntity' },
  { widgetId: 'event.eventType', label: 'Event Type', dataSource: 'inspectionEvent' },
  { widgetId: 'event.inspectionType', label: 'Inspection Type', dataSource: 'inspectionEvent' },
  { widgetId: 'event.status', label: 'Event Status', dataSource: 'inspectionEvent' },
  { widgetId: 'event.primaryInspectorName', label: 'Primary Inspector', dataSource: 'inspectionEvent' },
  { widgetId: 'event.scheduledDate', label: 'Scheduled Date', dataSource: 'inspectionEvent' },
  { widgetId: 'event.internalNotes', label: 'Internal Notes', dataSource: 'inspectionEvent' },
  { widgetId: 'event.externalNotes', label: 'External Notes', dataSource: 'inspectionEvent' },
  { widgetId: 'case.caseNumber', label: 'Case Number', dataSource: 'cases' },
  { widgetId: 'case.caseType', label: 'Case Type', dataSource: 'cases' },
  { widgetId: 'case.caseStatus', label: 'Case Status', dataSource: 'cases' },
  { widgetId: 'case.entityName', label: 'Entity Name (Case)', dataSource: 'cases' },
  { widgetId: 'case.entityAddress', label: 'Entity Address (Case)', dataSource: 'cases' },
];

const GENERATION_PROMPT = `You are building a complete inspection form structure based on a completed interview conversation.

Return ONLY a single valid JSON object — no markdown, no explanation, no wrapper.

Available data-bound widgets (use widgetId EXACTLY as listed — do not invent widgetId values):
${JSON.stringify(WIDGET_CATALOGUE, null, 2)}

Required output shape:
{
  "formName": "string — descriptive name for this form",
  "formDescription": "string — 1-2 sentences",
  "sections": [
    {
      "title": "string — section heading",
      "fields": [
        {
          "label": "string",
          "type": "text|toggle|number|textarea|dropdown|camera|signature|dataBound",
          "required": true,
          "widgetId": "entity.address.full or null if not a data-bound field",
          "dataSource": "inspectableEntity|inspectionEvent|cases or null"
        }
      ]
    }
  ]
}

Rules:
- Use type "dataBound" whenever a widgetId from the catalogue above applies — NEVER use plain text for entity address, parcel number, building type, inspector name, etc.
- Group related fields into logical sections (e.g. "Property Information", "Inspection Items", "Violations", "Signatures")
- For non-data-bound fields, set widgetId and dataSource to null
- camera fields: used for photo capture, no widgetId
- signature fields: used for inspector/owner signatures, no widgetId
- Produce a thorough, complete form — do not omit fields discussed in the conversation`;

// ---------------------------------------------------------------------------
// HTTP helpers — uses system-web apiFetch (not apiPost)
// ---------------------------------------------------------------------------

async function callConversationAgent(
  conversationHistory: SquirrelChatMessage[],
  userMessage: string,
  systemPrompt: string,
  context: SquirrelInterviewContext,
): Promise<string> {
  const res = await apiFetch('/api/ai/agent', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      message: userMessage,
      context: {
        surface: SURFACE_CONVERSATION,
        systemPrompt,
        conversationHistory,
        templateType: context.templateType,
        purpose: context.purpose,
      },
    }),
  }) as any;

  if (res.configured === false) {
    throw new Error('Azure OpenAI is not configured on the server. Please set AZURE_OPENAI_ENDPOINT and AZURE_OPENAI_API_KEY in system-api/.env.local and restart the server.');
  }

  return res.response ?? res.message ?? '';
}

export interface GeneratedFormField {
  label: string;
  type: string;
  required: boolean;
  widgetId: string | null;
  dataSource: string | null;
}

export interface GeneratedFormSection {
  title: string;
  fields: GeneratedFormField[];
}

export interface GeneratedFormResult {
  formName: string;
  formDescription: string;
  sections: GeneratedFormSection[];
}

async function callGenerationAgent(
  conversationHistory: SquirrelChatMessage[],
): Promise<GeneratedFormResult> {
  const res = await apiFetch('/api/ai/agent', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      message: 'Generate the complete inspection form structure based on everything we discussed.',
      context: {
        surface: SURFACE_GENERATION,
        systemPrompt: GENERATION_PROMPT,
        conversationHistory,
      },
    }),
  }) as any;

  const raw: string = res.response ?? res.message ?? '';
  if (!raw) throw new Error('Generation agent returned empty response');
  const jsonMatch = raw.match(/\{[\s\S]*\}/);
  if (!jsonMatch) throw new Error('Generation agent returned no JSON');
  return JSON.parse(jsonMatch[0]) as GeneratedFormResult;
}

export async function parseDocumentForTemplate(file: File): Promise<Array<{ label: string; type: string; required: boolean }>> {
  const formData = new FormData();
  formData.append('file', file);

  const response = await apiPostFormData<any>('/api/ai/parse-document', formData);

  return response.fields ?? [];
}

async function callExtractionAgent(
  conversationHistory: SquirrelChatMessage[],
): Promise<ExtractedMetadata> {
  const res = await apiFetch('/api/ai/agent', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      message: 'Extract template metadata from this completed conversation.',
      context: {
        surface: SURFACE_EXTRACTION,
        systemPrompt: EXTRACTION_PROMPT,
        conversationHistory,
      },
    }),
  }) as any;

  const raw: string = res.response ?? res.message ?? '';
  const jsonMatch = raw.match(/\{[\s\S]*\}/);
  if (!jsonMatch) throw new Error('Extraction agent returned no JSON');
  return JSON.parse(jsonMatch[0]) as ExtractedMetadata;
}

// ---------------------------------------------------------------------------
// SquirrelMessageSender — injected into <SquirrelInterviewModal messageSender={…} />
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// AI Metadata Generation — called on template publish
// ---------------------------------------------------------------------------

const AI_METADATA_PROMPT = `You generate discovery metadata for fire inspection templates.

Given a template name, description, type, and tags, return ONLY a valid JSON object:
{
  "aiDescription": "string — 1-2 sentences describing what this template inspects and who uses it",
  "aiTags": ["string"] — 5-10 lowercase hyphenated tags for search discovery (e.g. "defensible-space", "annual", "nfpa-1", "commercial", "wildland-urban-interface"),
  "aiSuggestedFor": ["string"] — 2-4 short phrases describing ideal use cases (e.g. "Annual commercial fire code inspections", "Pre-season wildland clearance")
}

Rules:
- No markdown, no explanation, no wrapper — just the JSON object
- aiTags should include: inspection type, occupancy/hazard type, applicable codes, frequency, geographic scope if relevant
- aiSuggestedFor should be actionable phrases a department admin would recognize`;

export async function generateTemplateAiMetadata(template: {
  name: string;
  description?: string;
  templateType: string;
  tags?: string[];
}): Promise<{ aiDescription: string; aiTags: string[]; aiSuggestedFor: string[] } | null> {
  try {
    const userMessage = `Template Name: ${template.name}
Description: ${template.description ?? 'None provided'}
Template Type: ${template.templateType}
Manual Tags: ${(template.tags ?? []).join(', ') || 'None'}

Generate AI metadata for this inspection template.`;

    const res = await apiFetch('/api/ai/agent', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        message: userMessage,
        context: { surface: SURFACE_EXTRACTION, systemPrompt: AI_METADATA_PROMPT },
      }),
    }) as any;

    const raw: string = res.response ?? res.message ?? '';
    const jsonMatch = raw.match(/\{[\s\S]*\}/);
    if (!jsonMatch) return null;
    return JSON.parse(jsonMatch[0]);
  } catch {
    return null;
  }
}

export async function squirrelMessageSender(
  conversationHistory: SquirrelChatMessage[],
  userMessage: string,
  context: SquirrelInterviewContext,
): Promise<SquirrelSendResult> {
  const systemPrompt = buildSystemPrompt(context);
  const response = await callConversationAgent(conversationHistory, userMessage, systemPrompt, context);

  const isComplete = response.includes('[SQUIRREL_COMPLETE]');
  const cleanResponse = response.replace('[SQUIRREL_COMPLETE]', '').trim();

  let metadata: ExtractedMetadata | undefined;
  if (isComplete) {
    const fullHistory: SquirrelChatMessage[] = [
      ...conversationHistory,
      { role: 'user', content: userMessage },
      { role: 'assistant', content: cleanResponse },
    ];
    metadata = await callExtractionAgent(fullHistory);
    metadata.visibility = 'system';
    try {
      (metadata as any).generatedForm = await callGenerationAgent(fullHistory);
    } catch (genErr) {
      console.warn('Generation agent failed, template will open with empty canvas:', genErr);
    }
  }

  return { response: cleanResponse, isComplete, metadata };
}
